import * as THREE from 'three';
import { buildInteriorShell, type ShellLayout, type ShellSurface } from '../render/interiorShell';
import yardLayout from '../render/interiors/yard.json';

/**
 * The profile page's set (src/profile): a lock-up yard at night, drawn INSTEAD of the city while the page is up
 * (render/sceneOverride.ts, as Pike's studio and Sid's back room). A brick back wall with the roller door raised
 * half way on a lit lock-up (a bare bulb, a bench, the shelves), a side wall, a tyre stack, a drum and a crate,
 * a wet concrete floor with puddles, the city's glow over the wall. Your motor stands in the yard and you stand
 * in front of it (the system puts both in; this file is only the place).
 *
 * Lit like a 1970 crime-film still, three sources and nothing else:
 *  - the sodium lamp over the door is the KEY — a warm directional from the lamp's direction that casts the one
 *    shadow (toward the camera, as the lamp would), with the lamp's own pool on the concrete, a halo, a glow cone
 *    with drizzle falling through it and moths round the bulb;
 *  - the sky is the FILL — a cool blue directional from the camera's side and a cool hemisphere, so the faces
 *    toward the camera are never black, only cold;
 *  - the lock-up's bulb is the SPILL — a warm point light in the doorway that catches the motor's tail, your
 *    shoulders and the threshold, with a wedge of light on the concrete.
 * The light SET mirrors the city's — two directional lights (one shadow), one hemisphere, one point light — and the
 * fog is the same kind (exp²), so the car's and the figure's materials keep their shader programs: opening the page
 * compiles nothing new for them. The paint and the chrome reflect an environment map built once per visit from a
 * sketch of this yard (yardEnvironment: the sodium lamp high behind, the cold sky, the warm doorway, the brick, the
 * city glow at the horizon), so the highlights sit where the lights are.
 *
 * Everything here is built on open and disposed on close; ~30 draws, a few small canvas textures, the walls and
 * the door as the baked shell (render/interiorShell.ts), the lock-up's inside vertex-lit (no light needed), no
 * per-frame allocation, and the vapour is animated on the GPU. By tier: HIGH a 1024² shadow map, MEDIUM 768², LOW
 * no real shadow, the floor and skyline canvases at half size without anisotropy, half the drizzle and the vapour;
 * the environment map (PMREM) stays on every tier so the car's materials keep their programs. The render system
 * does not adapt the tier or the pixel ratio while an interior is up, so the yard's cheap frames never lift the
 * ratio the city then has to drop again.
 *
 * Local frame: the camera at yaw 0 stands at −z and looks along +z; x runs across the yard, +x to the LEFT of the
 * picture (a right-handed camera looking down +z has +x on its left): the roller door sits screen-left behind
 * the motor, the side wall, tyres and drum screen-right behind you.
 */
export interface ProfileSet {
  readonly scene: THREE.Scene;
  /** Stand the set in the world: its origin and the yaw its local +z points along. */
  place(x: number, z: number, yaw: number): void;
  /** Local ground point → world. */
  toWorld(lx: number, lz: number, out: { x: number; z: number }): { x: number; z: number };
  /** Local heading → world heading. */
  heading(local: number): number;
  update(dt: number): void;
  dispose(): void;
}

/** Where the motor parks (nose toward the camera, three-quarter), where you stand, what the camera frames. */
export const CAR_SPOT = { x: 1.45, z: 2.9, heading: Math.PI + 0.62 };
export const FIGURE_SPOT = { x: -1.05, z: 0.3, heading: Math.PI - 0.28 };
export const FOCUS = { x: -0.15, z: 1.3 };
/** The lamp over the roller door (local). */
const LAMP = new THREE.Vector3(1.2, 3.95, 5.72);
/** The lock-up's inspection lamp, inside: a caged bulb hung on a nail by the bench, low enough to show under the shutter. */
const BULB = new THREE.Vector3(0.6, 1.05, 8.9);
/** The wall past the door, screen-left over the motor's roof: the bill, the boiler's vent, the dustbin (local). */
const POSTER = { x: 4.15, y: 1.95 };
const VENT = { x: 3.7, y: 0.36 };
const BIN = { x: 4.8, z: 5.35 };
/** Where the cat sits: on the bin's lid, or under the bill at the foot of the wall (past the vent, it is still in view beside the motor's tail). */
const CAT_SPOTS: readonly { x: number; y: number; z: number }[] = [
  { x: BIN.x + 0.02, y: 0.79, z: BIN.z - 0.05 },
  { x: 4.3, y: 0, z: 5.55 },
];
/** How high the roller door is raised (the bottom edge of the shutter, metres). */
const DOOR_RAISED = 1.5;
const DOOR_TOP = 3.0;
const BG = 0x07070b;
/** The key's full candela (sodium: 0xffa347 is a high-pressure sodium lamp warmed by the film grade). */
const KEY_POWER = 2.2;
/** The lamp as a point light too (candela): its falloff over the motor's roof, its reflection in the puddle. */
const LAMP_POWER = 30;

/** The tier's texture budget: LOW draws the big canvases (the floor, the skyline) at half size and samples them plainly. */
let texScale = 1;
let texAniso = 4;

/** A canvas drawn in `w × h` coordinates at `texScale` of that size. */
function canvasTexture(w: number, h: number, draw: (g: CanvasRenderingContext2D) => void, srgb = true, scaled = false): THREE.CanvasTexture | null {
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  const s = scaled ? texScale : 1;
  c.width = Math.round(w * s);
  c.height = Math.round(h * s);
  const g = c.getContext('2d');
  if (!g) return null;
  g.scale(s, s);
  draw(g);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = texAniso;
  return t;
}

/** The walls and the door: the baked tiles' repeat, their mean until loaded; the door spans its quad once. */
const SHELL_MATS = {
  stock: { tile: 'stock', size: 0.9, mean: 0x4a453b, color: 0xffffff, roughness: 0.92 },
  door: { tile: 'door', size: [3.6, 3.0] as [number, number], mean: 0x2f3a31, color: 0xffffff, roughness: 0.5, metalness: 0.3, envMapIntensity: 0.6 },
};
const LIGHTMAP_INTENSITY = 3.0;

/**
 * The yard's layout with the roller door raised: the door quad keeps only its top (DOOR_RAISED..DOOR_TOP), showing
 * that part of the tile and that part of its lightmap rect, and the back wall gets the doorway cut out of it (three
 * quads round the opening, each with its share of the wall's lightmap rect: the bake had the door shut, so the
 * brick behind it is unlit in the map), so the lock-up's inside shows beneath the shutter.
 */
function raisedDoorLayout(layout: ShellLayout): ShellLayout {
  const f = DOOR_RAISED / DOOR_TOP;
  const out: ShellSurface[] = [];
  for (const s of layout.surfaces) {
    const [p0, p1, p2, p3] = s.p as [number[], number[], number[], number[]];
    const [u0, v0, u1, v1] = s.lm as [number, number, number, number];
    if (s.name === 'door') {
      out.push({
        ...s,
        p: [
          [p0[0]!, DOOR_RAISED, p0[2]!],
          [p1[0]!, DOOR_RAISED, p1[2]!],
          [p2[0]!, p2[1]!, p2[2]!],
          [p3[0]!, p3[1]!, p3[2]!],
        ],
        lm: s.rot ? [u0 + (u1 - u0) * f, v0, u1, v1] : [u0, v0 + (v1 - v0) * f, u1, v1],
        uv: [0, f, 1, 1],
      });
    } else if (s.name === 'back') {
      // u runs p0 → p1 (from +x to −x), v up; a sub-quad takes the matching share of the lm rect
      const xL = p0[0]!;
      const xR = p1[0]!;
      const top = p3[1]!;
      const z = p0[2]!;
      const door = layout.surfaces.find((d) => d.name === 'door')!;
      const dL = door.p[0]![0]!;
      const dR = door.p[1]![0]!;
      const dT = door.p[2]![1]!;
      const fu = (x: number): number => (xL - x) / (xL - xR);
      const fv = (y: number): number => y / top;
      const piece = (name: string, xa: number, xb: number, ya: number, yb: number): ShellSurface => ({
        ...s,
        name,
        p: [
          [xa, ya, z],
          [xb, ya, z],
          [xb, yb, z],
          [xa, yb, z],
        ],
        lm: s.rot
          ? [u0 + (u1 - u0) * fv(ya), v0 + (v1 - v0) * fu(xa), u0 + (u1 - u0) * fv(yb), v0 + (v1 - v0) * fu(xb)]
          : [u0 + (u1 - u0) * fu(xa), v0 + (v1 - v0) * fv(ya), u0 + (u1 - u0) * fu(xb), v0 + (v1 - v0) * fv(yb)],
      });
      out.push(piece('back-left', xL, dL, 0, top), piece('back-right', dR, xR, 0, top), piece('back-over', dL, dR, dT, top));
    } else out.push(s);
  }
  return { ...layout, surfaces: out };
}

/** The number plate over the door: "7" on a cream enamel sign, LOCK-UP in small caps. */
function signTexture(): THREE.CanvasTexture | null {
  return canvasTexture(256, 128, (g) => {
    g.fillStyle = '#e4dbc4';
    g.fillRect(0, 0, 256, 128);
    g.strokeStyle = '#15120f';
    g.lineWidth = 6;
    g.strokeRect(6, 6, 244, 116);
    g.fillStyle = '#15120f';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = 'bold 86px Impact, "Arial Narrow", sans-serif';
    g.fillText('7', 128, 58);
    g.font = 'bold 20px "Courier New", monospace';
    g.fillText('LOCK-UP', 128, 110);
  });
}

/**
 * A fly-posted boxing bill on the back wall by the door, a few winters old: a sun-bleached yellow sheet over the
 * corner of an older blue one, torn edges, rain streaks, a missing corner. All of it made up (a hall and two
 * fighters nobody ever heard of). Alpha outside the paper, so the brick shows round the tears.
 */
function posterTexture(): THREE.CanvasTexture | null {
  const W = 256;
  const H = 384;
  return canvasTexture(W, H, (g) => {
    const rnd = lcg(0xb0c5);
    // a torn sheet: a rectangle whose edge wanders a few px, a bite out of one corner
    const sheet = (x0: number, y0: number, x1: number, y1: number, bite: 'tl' | 'br' | null): void => {
      g.beginPath();
      const step = 14;
      const j = (): number => (rnd() - 0.5) * 7;
      g.moveTo(x0 + j(), y0 + j());
      for (let x = x0 + step; x < x1; x += step) g.lineTo(x + j(), y0 + j());
      if (bite === 'br') g.lineTo(x1 + j(), y0 + j());
      for (let y = y0 + step; y < y1; y += step) {
        if (bite === 'br' && y > y1 - 70) break;
        g.lineTo(x1 + j(), y + j());
      }
      if (bite === 'br') {
        g.lineTo(x1 - 12 + j(), y1 - 58 + j());
        g.lineTo(x1 - 40 + j(), y1 - 30 + j());
        g.lineTo(x1 - 62 + j(), y1 + j());
      } else g.lineTo(x1 + j(), y1 + j());
      for (let x = x1 - step; x > x0; x -= step) g.lineTo(x + j(), y1 + j());
      g.lineTo(x0 + j(), y1 + j());
      for (let y = y1 - step; y > y0; y -= step) {
        if (bite === 'tl' && y < y0 + 50) break;
        g.lineTo(x0 + j(), y + j());
      }
      if (bite === 'tl') {
        g.lineTo(x0 + 10 + j(), y0 + 38 + j());
        g.lineTo(x0 + 34 + j(), y0 + 14 + j());
        g.lineTo(x0 + 46 + j(), y0 + j());
      }
      g.closePath();
    };
    // the older bill underneath: a faded blue dance-hall sheet, only its right-hand strip still up
    sheet(150, 20, 252, 370, null);
    g.fillStyle = '#5d7693';
    g.fill();
    g.fillStyle = '#e8e2cc';
    g.font = 'bold 26px "Arial Narrow", Arial, sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.save();
    g.translate(228, 190);
    g.rotate(-Math.PI / 2);
    g.fillText('SATURDAY DANCE · THE PALAIS', 0, 0);
    g.restore();
    // the boxing bill
    sheet(6, 8, 206, 376, 'br');
    g.save();
    g.clip();
    g.fillStyle = '#dfc265';
    g.fillRect(0, 0, W, H);
    // foxing and damp: brown clouds low down
    for (let i = 0; i < 14; i++) {
      g.fillStyle = `rgba(110,70,30,${0.05 + rnd() * 0.1})`;
      g.beginPath();
      g.ellipse(rnd() * W, 180 + rnd() * 200, 20 + rnd() * 50, 10 + rnd() * 30, rnd() * 3, 0, Math.PI * 2);
      g.fill();
    }
    g.fillStyle = '#1a1512';
    g.textAlign = 'center';
    g.textBaseline = 'alphabetic';
    g.font = 'bold 22px "Arial Narrow", Arial, sans-serif';
    g.fillText('ALBION BATHS HALL', 106, 42);
    g.fillStyle = '#a8231c';
    g.font = 'bold 74px Impact, "Arial Narrow", sans-serif';
    g.fillText('BOXING', 106, 112);
    g.fillStyle = '#1a1512';
    g.font = 'bold 20px "Arial Narrow", Arial, sans-serif';
    g.fillText('FRIDAY 8 pm · 8 ROUNDS', 106, 142);
    g.font = 'bold 44px Impact, "Arial Narrow", sans-serif';
    g.fillText('DANNY', 106, 196);
    g.fillText('McCREE', 106, 240);
    g.fillStyle = '#a8231c';
    g.font = 'bold italic 30px "Times New Roman", serif';
    g.fillText('v.', 106, 272);
    g.fillStyle = '#1a1512';
    g.font = 'bold 40px Impact, "Arial Narrow", sans-serif';
    g.fillText('BILLY CORRIGAN', 106, 316);
    g.font = 'bold 15px "Arial Narrow", Arial, sans-serif';
    g.fillText('TICKETS 50p · RINGSIDE 75p', 106, 352);
    // rain streaks down the paper, a bleached top edge
    for (let i = 0; i < 26; i++) {
      const x = rnd() * W;
      g.fillStyle = `rgba(255,250,230,${0.08 + rnd() * 0.16})`;
      g.fillRect(x, rnd() * 60, 1 + rnd() * 3, 120 + rnd() * 240);
    }
    const bleach = g.createLinearGradient(0, 0, 0, 120);
    bleach.addColorStop(0, 'rgba(255,252,240,0.45)');
    bleach.addColorStop(1, 'rgba(255,252,240,0)');
    g.fillStyle = bleach;
    g.fillRect(0, 0, W, 120);
    g.restore();
    // paste-brush shadow under the lifted edges
    g.strokeStyle = 'rgba(40,25,10,0.35)';
    g.lineWidth = 2;
    g.stroke();
  });
}

/**
 * The yard's cat as a silhouette atlas, 4 × 2 cells: two sitting poses (tail down, tail up) and a four-frame
 * walk, all facing −x (left in the cell); the card flips for the other way. Black against the sky's glow and the
 * lit wall, which is how a cat on a wall is seen at night anyway. CAT_CELL gives the head's centre per cell for
 * the eyes.
 */
const CAT_CELLS = 4;
const CAT_ROWS = 2;
const CAT_PX = 128;
/** Head centre (cell px) per cell, for the eyeshine: sitting cells only. */
const CAT_HEAD: Record<number, [number, number]> = { 0: [60, 40], 1: [60, 40] };
function catTexture(): THREE.CanvasTexture | null {
  const W = CAT_CELLS * CAT_PX;
  const H = CAT_ROWS * CAT_PX;
  return canvasTexture(W, H, (g) => {
    g.clearRect(0, 0, W, H);
    g.fillStyle = '#0a090c';
    g.strokeStyle = '#0a090c';
    g.lineCap = 'round';
    const ell = (x: number, y: number, rx: number, ry: number, rot = 0): void => {
      g.beginPath();
      g.ellipse(x, y, rx, ry, rot, 0, Math.PI * 2);
      g.fill();
    };
    const ears = (hx: number, hy: number, r: number): void => {
      g.beginPath();
      g.moveTo(hx - r * 0.9, hy - r * 0.2);
      g.lineTo(hx - r * 0.95, hy - r * 1.7);
      g.lineTo(hx - r * 0.15, hy - r * 0.9);
      g.closePath();
      g.fill();
      g.beginPath();
      g.moveTo(hx + r * 0.9, hy - r * 0.2);
      g.lineTo(hx + r * 0.75, hy - r * 1.75);
      g.lineTo(hx + r * 0.1, hy - r * 0.9);
      g.closePath();
      g.fill();
    };
    const tail = (x0: number, y0: number, cx: number, cy: number, x1: number, y1: number, w: number): void => {
      g.lineWidth = w;
      g.beginPath();
      g.moveTo(x0, y0);
      g.quadraticCurveTo(cx, cy, x1, y1);
      g.stroke();
    };
    // sitting (cells 0, 1): upright body, head toward us, front paws together, the tail down round the paws / up
    for (let c = 0; c < 2; c++) {
      g.save();
      g.translate(c * CAT_PX, 0);
      ell(60, 86, 23, 34); // body
      ell(60, 112, 26, 10); // haunches on the ground
      ell(50, 116, 6, 5);
      ell(68, 116, 6, 5); // paws
      ell(60, 40, 17, 16); // head
      ears(60, 40, 17);
      if (c === 0) tail(80, 110, 108, 118, 100, 90, 7);
      else tail(80, 108, 112, 100, 98, 62, 7);
      g.restore();
    }
    // walking (cells 2..5): low body, head forward (−x), tail up, four legs through a cycle
    for (let f = 0; f < 4; f++) {
      g.save();
      g.translate((f + 2) * CAT_PX, 0);
      const ph = (f / 4) * Math.PI * 2;
      const bob = Math.sin(ph * 2) * 1.5;
      ell(68, 78 + bob, 34, 15, 0.05); // body
      ell(36, 68 + bob, 14, 13); // head
      ears(36, 66 + bob, 13);
      ell(44, 78 + bob, 10, 8); // neck
      tail(100, 72 + bob, 118, 60, 108, 36 + Math.sin(ph) * 6, 6);
      g.lineWidth = 7;
      // legs: front pair and back pair out of phase, each leg a stroke from hip to paw
      const legs: [number, number, number][] = [
        [48, 0, 1],
        [56, Math.PI, 1],
        [84, Math.PI * 0.9, -1],
        [92, Math.PI * 1.9, -1],
      ];
      for (const [hx, off, side] of legs) {
        const swing = Math.sin(ph + off) * 9;
        const lift = Math.max(0, Math.cos(ph + off)) * 5;
        g.beginPath();
        g.moveTo(hx, 84 + bob);
        g.lineTo(hx + swing * 0.4 - side * 2, 98 + bob - lift * 0.5);
        g.lineTo(hx + swing, 110 - lift);
        g.stroke();
      }
      g.restore();
    }
  });
}

/**
 * Vapour out of a vent low in the back wall (the lock-up's boiler flue): a few soft puffs that rise, drift into the
 * yard and thin, lit warm from the sodium lamp above. Animated on the GPU from `uTime` (nothing per frame on the
 * CPU), billboarded quads so no point-size cap applies on a phone, one draw.
 */
const VapourShader = {
  uniforms: { uTime: { value: 0 }, uOrigin: { value: new THREE.Vector3() }, uColor: { value: new THREE.Color(0xcfb08a) }, uAlpha: { value: 0.1 } },
  vertexShader: /* glsl */ `
    attribute vec4 aSeed; // phase, sideways drift, outward drift, size
    uniform float uTime; uniform vec3 uOrigin;
    varying vec2 vUv; varying float vA;
    void main() {
      vUv = uv;
      float age = fract(uTime * 0.3 + aSeed.x);           // one puff every ~3.3 s per slot
      vec3 p = uOrigin;
      p.y += age * 1.0 + age * age * 0.5;                  // rises, a little faster as it thins
      p.x += aSeed.y * age * 1.1 + sin(uTime * 0.6 + aSeed.x * 6.2832) * 0.1 * age;
      p.z -= (0.5 + aSeed.z) * age * 1.1;                  // out of the wall into the yard
      vA = smoothstep(0.0, 0.12, age) * (1.0 - smoothstep(0.3, 1.0, age));
      float size = (0.22 + age * 0.9) * aSeed.w;
      vec4 mv = modelViewMatrix * vec4(p, 1.0);
      mv.xy += (position.xy) * size;                       // billboard: the quad in view space
      gl_Position = projectionMatrix * mv;
    }`,
  fragmentShader: /* glsl */ `
    uniform vec3 uColor; uniform float uAlpha;
    varying vec2 vUv; varying float vA;
    void main() {
      float d = length(vUv - 0.5) * 2.0;
      float a = pow(max(0.0, 1.0 - d), 1.8) * vA * uAlpha;
      gl_FragColor = vec4(uColor, a);
    }`,
};

/** A radial alpha gradient (white), for the lamp's pool, the halos, the contact shadows and the moths. */
function radial(n: number, power: number): THREE.DataTexture {
  const data = new Uint8Array(n * n * 4);
  for (let y = 0; y < n; y++)
    for (let x = 0; x < n; x++) {
      const d = Math.min(1, Math.hypot((x + 0.5) / n - 0.5, (y + 0.5) / n - 0.5) * 2);
      const i = (y * n + x) * 4;
      data[i] = data[i + 1] = data[i + 2] = 255;
      data[i + 3] = Math.round(Math.pow(1 - d, power) * 255);
    }
  const t = new THREE.DataTexture(data, n, n, THREE.RGBAFormat);
  t.needsUpdate = true;
  return t;
}

/** A small deterministic generator for the canvas art (the same yard every visit). */
function lcg(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

/**
 * The wet concrete: a map (puddles darker, a scatter of grit) and a roughness map (the puddles glassy, the dry
 * concrete dull), one tile repeated over the floor. Both drawn wrapped so the repeat never shows a seam.
 */
function floorTextures(): { map: THREE.CanvasTexture; rough: THREE.CanvasTexture } | null {
  const N = 512;
  const rnd = lcg(0x1970);
  const puddles: [number, number, number, number, number][] = [];
  for (let i = 0; i < 7; i++) puddles.push([rnd() * N, rnd() * N, 40 + rnd() * 90, 25 + rnd() * 50, rnd() * Math.PI]);
  const blobs = (g: CanvasRenderingContext2D, fill: string, soft: number): void => {
    for (const [x, y, rx, ry, a] of puddles)
      for (const ox of [-N, 0, N])
        for (const oy of [-N, 0, N]) {
          const grad = g.createRadialGradient(0, 0, 0, 0, 0, 1);
          grad.addColorStop(0, fill);
          grad.addColorStop(soft, fill);
          grad.addColorStop(1, 'rgba(0,0,0,0)');
          g.save();
          g.translate(x + ox, y + oy);
          g.rotate(a);
          g.scale(rx, ry);
          g.fillStyle = grad;
          g.beginPath();
          g.arc(0, 0, 1, 0, Math.PI * 2);
          g.fill();
          g.restore();
        }
  };
  const map = canvasTexture(
    N,
    N,
    (g) => {
    g.fillStyle = '#9a9690';
    g.fillRect(0, 0, N, N);
    // grit and stains
    const r2 = lcg(7);
    for (let i = 0; i < 2600; i++) {
      const v = 120 + Math.floor(r2() * 60);
      g.fillStyle = `rgba(${v},${v - 4},${v - 8},${0.25 + r2() * 0.4})`;
      g.fillRect(r2() * N, r2() * N, 1 + r2() * 3, 1 + r2() * 3);
    }
    for (let i = 0; i < 9; i++) {
      g.fillStyle = `rgba(40,34,30,${0.08 + r2() * 0.12})`;
      g.beginPath();
      g.ellipse(r2() * N, r2() * N, 30 + r2() * 90, 20 + r2() * 60, r2() * 3, 0, Math.PI * 2);
      g.fill();
    }
    // the puddles: darker where the water stands
    blobs(g, 'rgba(22,22,28,0.88)', 0.55);
    },
    true,
    true,
  );
  const rough = canvasTexture(
    N,
    N,
    (g) => {
      // three reads roughness from the green channel: dry concrete 0.72, the puddles 0.06
      g.fillStyle = 'rgb(0,184,0)';
      g.fillRect(0, 0, N, N);
      const r2 = lcg(11);
      for (let i = 0; i < 1400; i++) {
        g.fillStyle = `rgba(0,${150 + Math.floor(r2() * 60)},0,0.5)`;
        g.fillRect(r2() * N, r2() * N, 2 + r2() * 4, 2 + r2() * 4);
      }
      blobs(g, 'rgb(0,16,0)', 0.6);
    },
    false,
    true,
  );
  if (!map || !rough) return null;
  return { map, rough };
}

/**
 * The city over the wall: a glow at the horizon (sodium haze under a low sky) with the borough's roofline against
 * it — terraces, chimney stacks, a gasholder, a spire, a crane — and a few lit windows. One strip, repeated round.
 */
function skylineTexture(): THREE.CanvasTexture | null {
  const W = 1024;
  const H = 256;
  return canvasTexture(
    W,
    H,
    (g) => {
    const sky = g.createLinearGradient(0, 0, 0, H);
    sky.addColorStop(0, '#06060a');
    sky.addColorStop(0.45, '#0c0a10');
    sky.addColorStop(0.78, '#2a1a10');
    sky.addColorStop(1, '#4a2c12');
    g.fillStyle = sky;
    g.fillRect(0, 0, W, H);
    const rnd = lcg(0x7a2d);
    const ground = H * 0.78;
    g.fillStyle = '#050407';
    // terraces: runs of roofs at two depths, chimney stacks with pots
    let x = 0;
    while (x < W) {
      const w = 40 + rnd() * 90;
      const h = 22 + rnd() * 40;
      g.fillRect(x, ground - h, w + 1, h + 60);
      const stacks = 1 + Math.floor(rnd() * 3);
      for (let s = 0; s < stacks; s++) {
        const sx = x + 6 + rnd() * (w - 14);
        g.fillRect(sx, ground - h - 12, 7, 14);
        g.fillRect(sx + 1, ground - h - 17, 2, 6);
        g.fillRect(sx + 4, ground - h - 17, 2, 6);
      }
      // the odd lit window
      if (rnd() < 0.5) {
        g.fillStyle = `rgba(255,${170 + Math.floor(rnd() * 50)},90,${0.5 + rnd() * 0.4})`;
        g.fillRect(x + 8 + rnd() * (w - 16), ground - h + 8 + rnd() * (h - 12), 3, 4);
        g.fillStyle = '#050407';
      }
      x += w;
    }
    // a gasholder (the frame and the drum), a church spire, a dock crane
    const gx = 300;
    g.fillRect(gx, ground - 52, 120, 60);
    for (let i = 0; i <= 6; i++) g.fillRect(gx + i * 20 - 1, ground - 70, 3, 20);
    g.fillRect(gx - 2, ground - 70, 124, 2);
    g.beginPath();
    g.moveTo(760, ground - 30);
    g.lineTo(770, ground - 118);
    g.lineTo(780, ground - 30);
    g.fill();
    g.fillRect(752, ground - 40, 36, 50);
    g.fillRect(560, ground - 96, 4, 100);
    g.fillRect(520, ground - 96, 90, 3);
    g.fillRect(606, ground - 100, 4, 10);
    // the haze over the rooftops
    const haze = g.createLinearGradient(0, ground - 40, 0, ground + 10);
    haze.addColorStop(0, 'rgba(255,140,60,0)');
    haze.addColorStop(1, 'rgba(255,140,60,0.16)');
    g.fillStyle = haze;
    g.fillRect(0, ground - 40, W, 50);
    },
    true,
    true,
  );
}

/**
 * What the paint, the chrome and the puddles reflect: a sketch of the yard as light panels, PMREM'd once per
 * visit. Directions are the set's own (the camera looks along +z): the sodium lamp high behind the car (+z, up),
 * the cold sky overhead, the lock-up's warm doorway low at +z, dull brick at +z and −x, the city's sodium glow
 * round the horizon, black ground. Built in metres from the origin; only the directions and the sizes matter.
 */
function yardEnvironment(): THREE.Scene {
  const s = new THREE.Scene();
  const panel = (w: number, h: number, d: number, color: number, k: number, x: number, y: number, z: number, ry = 0): void => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), new THREE.MeshBasicMaterial({ color: new THREE.Color(color).multiplyScalar(k) }));
    m.position.set(x, y, z);
    m.rotation.y = ry;
    s.add(m);
  };
  // the sky: a cold dome, brighter toward the city glow at the horizon
  panel(40, 0.2, 40, 0x2c3a66, 0.9, 0, 12, 0);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    panel(14, 2.2, 0.2, 0x8a4a1c, 1.3, Math.sin(a) * 16, 1.4, Math.cos(a) * 16, a);
  }
  // the brick behind and beside, dull and warm under the lamp
  panel(20, 5, 0.2, 0x6a5a48, 0.42, 0, 2.5, 6.2);
  panel(0.2, 3, 14, 0x5a5048, 0.3, -6.3, 1.5, 0.5);
  // the sodium lamp: small, hot, high behind the car (the long highlight along the roof and the bonnet)
  panel(1.4, 0.5, 0.3, 0xffa347, 14, 1.2, 4.1, 5.5);
  panel(3.2, 1.6, 0.3, 0xff9a3f, 1.6, 1.2, 3.9, 5.7);
  // the lock-up's doorway: a warm slab low behind
  panel(3.4, 1.5, 0.3, 0xffc27a, 3.2, 1.2, 0.8, 6.0);
  // the wet ground: a faint cold sheen
  panel(40, 0.2, 40, 0x1a1c26, 0.35, 0, -2.5, 0);
  return s;
}

/** The sodium lamp's glow cone: brightest at the lamp, gone by the ground, fading where it is seen edge-on. */
const ConeShader = {
  uniforms: { uColor: { value: new THREE.Color(0xff9a3f) }, uK: { value: 0.035 } },
  vertexShader: /* glsl */ `
    varying float vT; varying vec3 vN; varying vec3 vV;
    void main() {
      vT = uv.y;
      vec4 mv = modelViewMatrix * vec4(position, 1.0);
      vN = normalize(normalMatrix * normal);
      vV = normalize(-mv.xyz);
      gl_Position = projectionMatrix * mv;
    }`,
  fragmentShader: /* glsl */ `
    uniform vec3 uColor; uniform float uK;
    varying float vT; varying vec3 vN; varying vec3 vV;
    void main() {
      float edge = clamp(abs(dot(normalize(vN), normalize(vV))), 0.001, 1.0);
      float a = pow(clamp(vT, 0.001, 1.0), 2.0) * pow(edge, 2.2) * uK;
      gl_FragColor = vec4(uColor * a, 1.0);
    }`,
};

const grime = lcg(0x6e1);

/** A quad lit by the bulb at BULB, vertex by vertex: albedo × (a little ambient + the bulb's inverse-square). */
function litQuad(w: number, h: number, seg: number, albedo: number, place: (g: THREE.BufferGeometry) => void): THREE.BufferGeometry {
  const g = new THREE.PlaneGeometry(w, h, seg, seg);
  place(g);
  g.computeVertexNormals();
  const pos = g.getAttribute('position');
  const nrm = g.getAttribute('normal');
  const col = new Float32Array(pos.count * 3);
  const base = new THREE.Color(albedo);
  const p = new THREE.Vector3();
  const n = new THREE.Vector3();
  const l = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    p.fromBufferAttribute(pos, i);
    n.fromBufferAttribute(nrm, i);
    l.subVectors(BULB, p);
    const d2 = l.lengthSq();
    // wrapped lambert (a grazing wall is dim, not black) over a softened inverse square: a bulb in a small room;
    // a little grime per vertex so the distemper is mottled, not a flat card
    const lambert = 0.3 + 0.7 * Math.max(0, l.normalize().dot(n));
    const k = (0.035 + (0.9 * lambert) / (1 + d2 * 0.5)) * (0.82 + 0.18 * grime());
    col[i * 3] = base.r * k * 1.0;
    col[i * 3 + 1] = base.g * k * 0.8;
    col[i * 3 + 2] = base.b * k * 0.52;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return g;
}

/** A box lit by the bulb (six lit quads), for the bench, the shelves and the odds on them. */
function litBox(w: number, h: number, d: number, albedo: number, x: number, y: number, z: number, ry = 0): THREE.BufferGeometry[] {
  const m = new THREE.Matrix4().makeRotationY(ry).setPosition(x, y, z);
  const faces: [number, number, (g: THREE.BufferGeometry) => void][] = [
    [w, h, (g) => g.translate(0, 0, d / 2)],
    [w, h, (g) => g.rotateY(Math.PI).translate(0, 0, -d / 2)],
    [d, h, (g) => g.rotateY(Math.PI / 2).translate(w / 2, 0, 0)],
    [d, h, (g) => g.rotateY(-Math.PI / 2).translate(-w / 2, 0, 0)],
    [w, d, (g) => g.rotateX(-Math.PI / 2).translate(0, h / 2, 0)],
  ];
  return faces.map(([fw, fh, place]) =>
    litQuad(fw, fh, 1, albedo, (g) => {
      place(g);
      g.applyMatrix4(m);
    }),
  );
}

export type ProfileTier = 'low' | 'medium' | 'high';

export function buildProfileSet(renderer: THREE.WebGLRenderer | null, tier: ProfileTier): ProfileSet {
  const shadows = tier !== 'low';
  const simple = tier === 'low';
  texScale = simple ? 0.5 : 1;
  texAniso = simple ? 1 : 4;
  const scene = new THREE.Scene();
  scene.name = 'profile-lockup';
  scene.background = new THREE.Color(BG);
  scene.fog = new THREE.FogExp2(BG, 0.03);
  const disposables: { dispose(): void }[] = [];
  const keep = <T extends { dispose(): void }>(d: T): T => {
    disposables.push(d);
    return d;
  };
  const rig = new THREE.Group();
  rig.name = 'set';
  scene.add(rig);
  const geos: THREE.BufferGeometry[] = [];
  const box = (w: number, h: number, d: number): THREE.BoxGeometry => {
    const g = new THREE.BoxGeometry(w, h, d);
    geos.push(g);
    return g;
  };
  const glowTex = keep(radial(64, 1.7));
  const softTex = keep(radial(64, 1.1));

  // ---- the concrete: wet, darker away from the lamp (vertex colours), puddles in its map and roughness map
  const floorGeo = keep(new THREE.PlaneGeometry(44, 44, 12, 12).rotateX(-Math.PI / 2));
  {
    const pos = floorGeo.getAttribute('position');
    const col = new Float32Array(pos.count * 3);
    const near = new THREE.Color(0x3a3632);
    const far = new THREE.Color(0x0c0b0e);
    const c = new THREE.Color();
    for (let i = 0; i < pos.count; i++) {
      const d = Math.hypot(pos.getX(i) - LAMP.x, pos.getZ(i) - 3.2);
      c.copy(near).lerp(far, Math.min(1, d / 15));
      col.set([c.r, c.g, c.b], i * 3);
    }
    floorGeo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  }
  const floorTex = floorTextures();
  if (floorTex) {
    keep(floorTex.map);
    keep(floorTex.rough);
    floorTex.map.repeat.set(44 / 9, 44 / 9);
    floorTex.rough.repeat.set(44 / 9, 44 / 9);
  }
  const floorMat = keep(
    new THREE.MeshStandardMaterial({
      vertexColors: true,
      map: floorTex?.map ?? null,
      roughnessMap: floorTex?.rough ?? null,
      roughness: floorTex ? 1 : 0.5,
      metalness: 0,
      envMapIntensity: 1.8,
    }),
  );
  const floor = new THREE.Mesh(floorGeo, floorMat);
  floor.receiveShadow = true;
  rig.add(floor);

  // ---- walls and the roller door (raised half way): the baked shell, a coping on the side wall
  const shell = keep(buildInteriorShell(raisedDoorLayout(yardLayout as ShellLayout), { specs: SHELL_MATS, lightMapIntensity: LIGHTMAP_INTENSITY, load: !!renderer }));
  rig.add(shell.mesh);
  const copingMat = keep(new THREE.MeshStandardMaterial({ color: 0x6e665c, roughness: 0.85 }));
  const coping = new THREE.Mesh(box(0.56, 0.12, 13.1), copingMat);
  coping.position.set(-6.4, 3.06, 0.5);
  rig.add(coping);

  // ---- the door's frame, its bottom rail where it stands raised, and the number plate
  const steel = keep(new THREE.MeshStandardMaterial({ color: 0x26302a, roughness: 0.6, metalness: 0.4 }));
  for (const sx of [-1, 1]) {
    const post = new THREE.Mesh(box(0.18, 3.1, 0.14), steel);
    post.position.set(1.2 + sx * 1.86, 1.55, 5.97);
    rig.add(post);
  }
  const lintel = new THREE.Mesh(box(4.1, 0.26, 0.18), steel);
  lintel.position.set(1.2, 3.17, 5.96);
  rig.add(lintel);
  const rail = new THREE.Mesh(box(3.6, 0.1, 0.12), steel);
  rail.position.set(1.2, DOOR_RAISED + 0.04, 5.98);
  rig.add(rail);
  const signTex = signTexture();
  if (signTex) keep(signTex);
  const signMat = keep(new THREE.MeshStandardMaterial({ color: signTex ? 0xffffff : 0xe4dbc4, map: signTex, roughness: 0.5 }));
  const signGeo = keep(new THREE.PlaneGeometry(0.9, 0.45));
  const sign = new THREE.Mesh(signGeo, signMat);
  // Beside the door at eye height, past where you stand so your head never covers it.
  sign.position.set(-2.7, 2.15, 5.98);
  sign.rotation.y = Math.PI; // the plane faces +z by default: turn it to the yard
  rig.add(sign);

  // ---- inside the lock-up: a shoebox lit by its bare bulb (vertex colours, no light), a bench, shelves, odds
  {
    const parts: THREE.BufferGeometry[] = [];
    const X0 = -0.6;
    const X1 = 3.0;
    const Z0 = 6.0;
    const Z1 = 9.6;
    const H = 2.9;
    const cx = (X0 + X1) / 2;
    const w = X1 - X0;
    const d = Z1 - Z0;
    parts.push(litQuad(w, H, 6, 0xb8a98c, (g) => g.rotateY(Math.PI).translate(cx, H / 2, Z1))); // back wall (distempered, grubby)
    parts.push(litQuad(d, H, 6, 0xb0a288, (g) => g.rotateY(Math.PI / 2).translate(X0, H / 2, (Z0 + Z1) / 2))); // left wall (faces +x)
    parts.push(litQuad(d, H, 6, 0xb0a288, (g) => g.rotateY(-Math.PI / 2).translate(X1, H / 2, (Z0 + Z1) / 2))); // right wall
    parts.push(litQuad(w, d, 8, 0x4a4742, (g) => g.rotateX(-Math.PI / 2).translate(cx, 0.005, (Z0 + Z1) / 2))); // concrete floor
    parts.push(litQuad(w, d, 6, 0x5a5650, (g) => g.rotateX(Math.PI / 2).translate(cx, H, (Z0 + Z1) / 2))); // ceiling
    parts.push(...litBox(1.6, 0.08, 0.7, 0x6a4a2c, 2.1, 0.86, 9.1)); // bench top
    parts.push(...litBox(0.1, 0.84, 0.6, 0x4a3420, 1.4, 0.42, 9.1), ...litBox(0.1, 0.84, 0.6, 0x4a3420, 2.8, 0.42, 9.1)); // its legs
    parts.push(...litBox(1.8, 0.05, 0.3, 0x6a4a2c, -0.3, 1.5, 7.6, Math.PI / 2), ...litBox(1.8, 0.05, 0.3, 0x6a4a2c, -0.3, 2.1, 7.6, Math.PI / 2)); // shelves on the left wall
    parts.push(...litBox(0.22, 0.3, 0.22, 0x9a2a22, -0.35, 1.68, 7.2), ...litBox(0.18, 0.24, 0.18, 0x2a4a8a, -0.35, 1.65, 7.7), ...litBox(0.3, 0.2, 0.2, 0xc8b890, -0.35, 2.23, 8.0)); // tins and a box
    parts.push(...litBox(0.4, 0.5, 0.4, 0x7a6a4a, 2.4, 0.25, 7.1), ...litBox(0.36, 0.32, 0.36, 0x8a7a58, 2.42, 0.66, 7.1)); // crates by the door
    parts.push(...litBox(0.5, 0.16, 0.5, 0x15161a, 0.1, 0.08, 8.8), ...litBox(0.5, 0.16, 0.5, 0x15161a, 0.12, 0.24, 8.78)); // spare tyres
    parts.push(...litBox(0.5, 0.7, 0.02, 0xd8c8a0, 1.2, 1.9, 9.59)); // a calendar on the back wall
    // one geometry, one draw
    let count = 0;
    for (const p of parts) count += p.getAttribute('position').count;
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    const idx: number[] = [];
    let at = 0;
    for (const p of parts) {
      const pp = p.getAttribute('position');
      const pc = p.getAttribute('color');
      const pi = p.getIndex();
      pos.set(pp.array as Float32Array, at * 3);
      col.set(pc.array as Float32Array, at * 3);
      if (pi) for (let i = 0; i < pi.count; i++) idx.push(pi.getX(i) + at);
      at += pp.count;
      p.dispose();
    }
    const g = keep(new THREE.BufferGeometry());
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    g.setIndex(idx);
    const inside = new THREE.Mesh(g, keep(new THREE.MeshBasicMaterial({ vertexColors: true })));
    inside.name = 'lockup-inside';
    rig.add(inside);
    // the bulb on its flex, and its halo
    const bulbMat = keep(new THREE.MeshBasicMaterial({ color: 0xfff1cc, fog: false }));
    const bulb = new THREE.Mesh(keep(new THREE.SphereGeometry(0.055, 10, 8)), bulbMat);
    bulb.position.copy(BULB);
    rig.add(bulb);
    // its cage and the flex up to the nail
    const cage = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.075, 0.075, 0.2, 8, 1, true)), keep(new THREE.MeshBasicMaterial({ color: 0x2a2420, wireframe: true })));
    cage.position.copy(BULB);
    rig.add(cage);
    const flex = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.008, 0.008, 0.5, 5)), keep(new THREE.MeshBasicMaterial({ color: 0x1a1612 })));
    flex.position.set(BULB.x, BULB.y + 0.33, BULB.z + 0.12);
    rig.add(flex);
    const haloMat = keep(new THREE.SpriteMaterial({ map: glowTex, color: 0xffc27a, transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending, depthWrite: false, fog: false }));
    const halo = new THREE.Sprite(haloMat);
    halo.position.copy(BULB);
    halo.scale.set(0.9, 0.9, 1);
    rig.add(halo);
  }
  // the light the doorway throws on the concrete: a wedge, warm at the threshold, gone by three metres
  {
    const g = keep(new THREE.PlaneGeometry(4.6, 3.4, 8, 8).rotateX(-Math.PI / 2).translate(1.2, 0.007, 6 - 1.7));
    const pos = g.getAttribute('position');
    const col = new Float32Array(pos.count * 3);
    const warm = new THREE.Color(0xffb866);
    for (let i = 0; i < pos.count; i++) {
      const dz = (6 - pos.getZ(i)) / 3.4; // 0 at the threshold, 1 at the far edge
      const dx = Math.abs(pos.getX(i) - 1.2) / (1.8 + dz * 0.5);
      const k = Math.pow(Math.max(0, 1 - dz), 2.2) * Math.max(0, 1 - dx * dx) * 0.55;
      col[i * 3] = warm.r * k;
      col[i * 3 + 1] = warm.g * k;
      col[i * 3 + 2] = warm.b * k;
    }
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    const wedge = new THREE.Mesh(g, keep(new THREE.MeshBasicMaterial({ vertexColors: true, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true, fog: false })));
    wedge.renderOrder = 1;
    rig.add(wedge);
  }

  // ---- the sodium lamp: a hooded fixture on the wall, a hot bulb, its halo and glow cone, its pool on the ground
  const hood = new THREE.Mesh(box(0.56, 0.16, 0.34), steel);
  hood.position.set(LAMP.x, LAMP.y + 0.12, LAMP.z + 0.04);
  rig.add(hood);
  const bulbMat = keep(new THREE.MeshBasicMaterial({ color: 0xffc889, fog: false }));
  const bulbGeo = keep(new THREE.SphereGeometry(0.085, 10, 8));
  const bulb = new THREE.Mesh(bulbGeo, bulbMat);
  bulb.position.copy(LAMP);
  rig.add(bulb);
  const lampHaloMat = keep(new THREE.SpriteMaterial({ map: glowTex, color: 0xff9a3f, transparent: true, opacity: 0.8, blending: THREE.AdditiveBlending, depthWrite: false, fog: false }));
  const lampHalo = new THREE.Sprite(lampHaloMat);
  lampHalo.position.copy(LAMP).add(new THREE.Vector3(0, -0.02, -0.1));
  lampHalo.scale.set(2.2, 2.2, 1);
  rig.add(lampHalo);
  const coneMat = keep(new THREE.ShaderMaterial({ ...ConeShader, uniforms: THREE.UniformsUtils.clone(ConeShader.uniforms), transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }));
  const coneGeo = keep(new THREE.CylinderGeometry(0.1, 2.1, LAMP.y - 0.05, 28, 1, true));
  const cone = new THREE.Mesh(coneGeo, coneMat);
  cone.position.set(LAMP.x, (LAMP.y - 0.05) / 2, LAMP.z - 0.6);
  cone.rotation.x = 0.17; // tilted a touch out into the yard, as the hood throws it
  cone.renderOrder = 2;
  rig.add(cone);
  const poolMat = keep(new THREE.MeshBasicMaterial({ map: glowTex, color: 0xff9a45, transparent: true, opacity: 0.2, blending: THREE.AdditiveBlending, depthWrite: false, fog: false }));
  const poolGeo = keep(new THREE.PlaneGeometry(8.5, 6.5).rotateX(-Math.PI / 2));
  const pool = new THREE.Mesh(poolGeo, poolMat);
  pool.position.set(LAMP.x, 0.006, 3.4);
  pool.renderOrder = 1;
  rig.add(pool);

  // ---- contact shadows: under the motor and under your feet (the real shadow is the key's; these are the dark
  // where the ground meets a thing, which a shadow map never gives)
  const contactMat = keep(new THREE.MeshBasicMaterial({ map: softTex, color: 0x000000, transparent: true, opacity: 0.78, depthWrite: false, fog: false }));
  const contactGeo = keep(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2));
  const carShadow = new THREE.Mesh(contactGeo, contactMat);
  carShadow.position.set(CAR_SPOT.x, 0.004, CAR_SPOT.z);
  carShadow.rotation.y = CAR_SPOT.heading;
  carShadow.scale.set(2.7, 1, 5.4);
  carShadow.renderOrder = 1;
  rig.add(carShadow);
  const feetShadow = new THREE.Mesh(contactGeo, contactMat);
  feetShadow.position.set(FIGURE_SPOT.x, 0.005, FIGURE_SPOT.z);
  feetShadow.scale.set(1.0, 1, 0.8);
  feetShadow.renderOrder = 1;
  rig.add(feetShadow);

  // ---- the yard's odds and ends: a tyre stack, an oil drum, a crate, a drain
  const rubber = keep(new THREE.MeshStandardMaterial({ color: 0x15161a, roughness: 0.9 }));
  const tyreGeo = keep(new THREE.TorusGeometry(0.33, 0.13, 8, 20).rotateX(Math.PI / 2));
  for (let i = 0; i < 3; i++) {
    const t = new THREE.Mesh(tyreGeo, rubber);
    t.position.set(-4.9 + (i % 2) * 0.05, 0.13 + i * 0.26, 4.2 - (i % 2) * 0.04);
    t.rotation.y = i * 0.7;
    t.castShadow = shadows;
    rig.add(t);
  }
  const rust = keep(new THREE.MeshStandardMaterial({ color: 0x5a3b26, roughness: 0.8, metalness: 0.25 }));
  const drumGeo = keep(new THREE.CylinderGeometry(0.3, 0.3, 0.88, 14));
  const drum = new THREE.Mesh(drumGeo, rust);
  drum.position.set(-5.3, 0.44, 1.7);
  drum.castShadow = shadows;
  rig.add(drum);
  const wood = keep(new THREE.MeshStandardMaterial({ color: 0x5a4630, roughness: 0.9 }));
  const crate = new THREE.Mesh(box(0.72, 0.5, 0.5), wood);
  crate.position.set(-4.3, 0.25, 5.4);
  crate.rotation.y = 0.3;
  crate.castShadow = shadows;
  rig.add(crate);
  // a puddle in the foreground where the sodium lamp's reflection falls from the camera's eye (its mirror point in the
  // ground from the shot's camera: the lamp's point light puts its highlight there)
  const puddleGeo = keep(new THREE.CircleGeometry(1.2, 28).rotateX(-Math.PI / 2));
  const puddleMat = keep(new THREE.MeshStandardMaterial({ color: 0x0a0a0f, roughness: 0.09, metalness: 0, envMapIntensity: 1.6, transparent: true, opacity: 0.9 }));
  const puddle = new THREE.Mesh(puddleGeo, puddleMat);
  puddle.position.set(0.5, 0.004, -1.7);
  puddle.scale.set(1.7, 1, 0.9);
  puddle.rotation.y = 0.3;
  puddle.renderOrder = 1;
  rig.add(puddle);
  const drain = new THREE.Mesh(box(0.5, 0.03, 0.5), keep(new THREE.MeshStandardMaterial({ color: 0x101012, roughness: 0.7, metalness: 0.5 })));
  drain.position.set(-3.6, 0.012, -1.2);
  rig.add(drain);

  // ---- the wall past the door (screen-left, over the motor's roof): a fly-posted bill, the boiler's vent with its
  // vapour, a galvanised dustbin, and the yard's cat
  const posterTex = posterTexture();
  if (posterTex) {
    keep(posterTex);
    posterTex.wrapS = posterTex.wrapT = THREE.ClampToEdgeWrapping;
    const posterMat = keep(new THREE.MeshStandardMaterial({ map: posterTex, transparent: true, alphaTest: 0.5, roughness: 0.95 }));
    const poster = new THREE.Mesh(keep(new THREE.PlaneGeometry(0.7, 1.05)), posterMat);
    poster.position.set(POSTER.x, POSTER.y, 5.985);
    poster.rotation.set(0, Math.PI, 0.03); // pasted a touch askew, facing the yard
    rig.add(poster);
  }
  const grille = new THREE.Mesh(box(0.34, 0.2, 0.05), steel);
  grille.position.set(VENT.x, VENT.y, 5.985);
  rig.add(grille);
  for (let i = 0; i < 3; i++) {
    // the slats: dark gaps across the grille
    const slat = new THREE.Mesh(box(0.28, 0.03, 0.02), keep(new THREE.MeshBasicMaterial({ color: 0x050405 })));
    slat.position.set(VENT.x, VENT.y - 0.05 + i * 0.05, 5.955);
    rig.add(slat);
  }
  const zinc = keep(new THREE.MeshStandardMaterial({ color: 0x4e5452, roughness: 0.68, metalness: 0.6, envMapIntensity: 0.45 }));
  const bin = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.24, 0.21, 0.72, 14)), zinc);
  bin.position.set(BIN.x, 0.36, BIN.z);
  bin.castShadow = shadows;
  rig.add(bin);
  const lid = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.27, 0.25, 0.07, 14)), zinc);
  lid.position.set(BIN.x + 0.02, 0.755, BIN.z - 0.01);
  lid.rotation.z = 0.04;
  rig.add(lid);

  // the vapour: a few soft quads animated on the GPU (VapourShader), fewer on LOW
  const PUFFS = simple ? 10 : 22;
  const vapourGeo = keep(new THREE.InstancedBufferGeometry());
  {
    const quad = new THREE.PlaneGeometry(1, 1);
    vapourGeo.setAttribute('position', quad.getAttribute('position'));
    vapourGeo.setAttribute('uv', quad.getAttribute('uv'));
    vapourGeo.setIndex(quad.getIndex());
    const rnd = lcg(0x5ea);
    const seed = new Float32Array(PUFFS * 4);
    for (let i = 0; i < PUFFS; i++) seed.set([i / PUFFS + rnd() * 0.02, (rnd() - 0.5) * 0.8, rnd() * 0.6, 0.7 + rnd() * 0.6], i * 4);
    vapourGeo.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seed, 4));
    vapourGeo.instanceCount = PUFFS;
    vapourGeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(VENT.x, VENT.y + 0.9, 5.2), 2.2);
  }
  const vapourMat = keep(new THREE.ShaderMaterial({ ...VapourShader, uniforms: THREE.UniformsUtils.clone(VapourShader.uniforms), transparent: true, depthWrite: false }));
  vapourMat.uniforms.uOrigin!.value.set(VENT.x, VENT.y, 5.93);
  const vapour = new THREE.Mesh(vapourGeo, vapourMat);
  vapour.renderOrder = 2;
  rig.add(vapour);

  // the cat: a silhouette card facing the yard, two eyes that catch the light when it sits
  const catTex = catTexture();
  const catMat = keep(new THREE.MeshBasicMaterial({ map: catTex, color: 0x0a090c, transparent: true, alphaTest: 0.5, side: THREE.DoubleSide, fog: false }));
  if (catTex) {
    keep(catTex);
    catTex.wrapS = catTex.wrapT = THREE.ClampToEdgeWrapping;
    catTex.repeat.set(1 / CAT_CELLS, 1 / CAT_ROWS);
  }
  const CAT_SIZE = 0.7;
  const cat = new THREE.Mesh(keep(new THREE.PlaneGeometry(CAT_SIZE, CAT_SIZE)), catMat);
  cat.rotation.y = Math.PI; // faces −z, toward the camera
  cat.visible = !!catTex;
  rig.add(cat);
  const eyeGeo = keep(new THREE.BufferGeometry());
  const eyePos = new Float32Array(6);
  eyeGeo.setAttribute('position', new THREE.BufferAttribute(eyePos, 3).setUsage(THREE.DynamicDrawUsage));
  eyeGeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(BIN.x, 1, BIN.z), 3);
  const eyeMat = keep(new THREE.PointsMaterial({ map: softTex, color: 0xc8ffa0, size: 0.065, transparent: true, opacity: 0.95, blending: THREE.AdditiveBlending, depthWrite: false, fog: false }));
  const eyes = new THREE.Points(eyeGeo, eyeMat);
  eyes.renderOrder = 3;
  rig.add(eyes);
  const catRnd = lcg(0xca7);
  /** Where it is and what it is doing: sitting on the bin's lid or by the warm vent, or walking between. */
  const catState = {
    mode: 'sit' as 'sit' | 'walk',
    spot: 0 as 0 | 1, // 0 the bin lid, 1 by the vent
    timer: 6 + catRnd() * 10,
    x: CAT_SPOTS[0]!.x,
    y: CAT_SPOTS[0]!.y,
    z: CAT_SPOTS[0]!.z,
    from: CAT_SPOTS[0]!,
    to: CAT_SPOTS[1]!,
    walkT: 0,
    walkDur: 1,
    tailTimer: 2,
    tailUp: false,
    blink: 3,
  };
  const catFrame = (cell: number): void => {
    if (catTex) catTex.offset.set((cell % CAT_CELLS) / CAT_CELLS, 1 - (Math.floor(cell / CAT_CELLS) + 1) / CAT_ROWS);
  };
  catFrame(0);

  // ---- the city over the wall: a glow at the horizon with the roofline against it, round the whole yard
  const skyTex = skylineTexture();
  if (skyTex) {
    keep(skyTex);
    skyTex.repeat.set(3, 1);
    const skyGeo = keep(new THREE.CylinderGeometry(42, 42, 30, 48, 1, true));
    const sky = new THREE.Mesh(skyGeo, keep(new THREE.MeshBasicMaterial({ map: skyTex, side: THREE.BackSide, fog: false })));
    sky.position.y = 15 - 3.5;
    rig.add(sky);
  }

  // ---- drizzle in the lamp's light: short streaks, bright near the cone's axis, dim away from it
  const RAIN = simple ? 140 : 320;
  const rainPos = new Float32Array(RAIN * 6);
  const rainVel = new Float32Array(RAIN);
  const rainGeo = keep(new THREE.BufferGeometry());
  {
    const rnd = lcg(0x5e3);
    const col = new Float32Array(RAIN * 6);
    for (let i = 0; i < RAIN; i++) {
      const x = LAMP.x + (rnd() - 0.5) * 7;
      const z = LAMP.z - 0.8 - rnd() * 6;
      const y = rnd() * LAMP.y;
      rainPos.set([x, y, z, x, y - 0.42, z], i * 6);
      rainVel[i] = 5.5 + rnd() * 2.5;
      // how far from the lamp's cone (its axis, widening downward): outside it the streak is nearly invisible
      const r = Math.hypot(x - LAMP.x, z - (LAMP.z - 0.6)) / (0.3 + (1 - y / LAMP.y) * 2.4);
      const k = (0.14 + 1.0 * Math.max(0, 1 - r * r)) * (0.6 + 0.4 * rnd());
      col.set([k, k * 0.85, k * 0.7, k * 0.7, k * 0.6, k * 0.5], i * 6);
    }
    rainGeo.setAttribute('position', new THREE.BufferAttribute(rainPos, 3).setUsage(THREE.DynamicDrawUsage));
    rainGeo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    rainGeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(LAMP.x, 2, 2.5), 8);
  }
  const rainMat = keep(new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false, fog: false }));
  const rain = new THREE.LineSegments(rainGeo, rainMat);
  rain.renderOrder = 3;
  rig.add(rain);

  // ---- moths round the lamp
  const MOTHS = 7;
  const mothPos = new Float32Array(MOTHS * 3);
  const mothGeo = keep(new THREE.BufferGeometry());
  mothGeo.setAttribute('position', new THREE.BufferAttribute(mothPos, 3).setUsage(THREE.DynamicDrawUsage));
  mothGeo.boundingSphere = new THREE.Sphere(LAMP.clone(), 1.5);
  const mothMat = keep(new THREE.PointsMaterial({ map: softTex, color: 0xffe2b4, size: 0.07, transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending, depthWrite: false, fog: false }));
  const moths = new THREE.Points(mothGeo, mothMat);
  moths.renderOrder = 3;
  rig.add(moths);
  const mothSeed = Array.from({ length: MOTHS }, (_, i) => ({ a: i * 1.7, b: i * 0.9 + 0.4, ra: 0.25 + (i % 3) * 0.14, rb: 0.18 + ((i * 7) % 4) * 0.08, s: 1.6 + (i % 4) * 0.5 }));

  // ---- the lights (the city's set: 2 directional, 1 hemisphere, 1 point — see above)
  // the key: the sodium lamp, from its direction, the one shadow (toward the camera)
  const key = new THREE.DirectionalLight(0xffa347, KEY_POWER);
  key.position.set(2.6, 7.4, 9.6);
  key.target.position.set(0, 0.5, 1.6);
  key.castShadow = shadows;
  if (shadows) {
    key.shadow.mapSize.set(tier === 'high' ? 1024 : 768, tier === 'high' ? 1024 : 768);
    const sc = key.shadow.camera;
    sc.left = -7;
    sc.right = 7;
    sc.top = 7;
    sc.bottom = -7;
    sc.near = 1;
    sc.far = 30;
    key.shadow.bias = -0.0008;
    key.shadow.normalBias = 0.03;
    key.shadow.radius = 4;
  }
  // the fill: the night sky, cold, from the camera's side, so the faces toward us are cool and never black
  const fill = new THREE.DirectionalLight(0x6a86c8, 0.7);
  fill.position.set(-6, 5, -7);
  fill.target.position.set(0, 0.8, 1.5);
  const hemi = new THREE.HemisphereLight(0x27304f, 0x15110d, 0.5);
  // the lamp itself as a point light: the falloff over the motor's roof and its highlight in the puddle (the lock-up's
  // spill is the inside's own brightness, the wedge on the concrete and the key: no light to spare for it)
  const lamp = new THREE.PointLight(0xff9a3f, LAMP_POWER, 20, 1.5);
  lamp.position.copy(LAMP).add(new THREE.Vector3(0, -0.05, -0.35));
  rig.add(key, key.target, fill, fill.target, hemi, lamp);

  // The environment the paint, the chrome and the puddles reflect: this yard, sketched (see yardEnvironment).
  if (renderer) {
    const pmrem = new THREE.PMREMGenerator(renderer);
    const env = yardEnvironment();
    const rt = pmrem.fromScene(env, 0.035);
    env.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
      }
    });
    pmrem.dispose();
    scene.environment = rt.texture;
    scene.environmentIntensity = 0.75;
    keep(rt);
  }

  let t = 0;
  const w = { x: 0, z: 0, yaw: 0 };
  return {
    scene,
    place(x, z, yaw) {
      w.x = x;
      w.z = z;
      w.yaw = yaw;
      rig.position.set(x, 0, z);
      rig.rotation.y = yaw;
      rig.updateMatrixWorld(true);
    },
    toWorld(lx, lz, out) {
      const s = Math.sin(w.yaw);
      const c = Math.cos(w.yaw);
      out.x = w.x + lx * c + lz * s;
      out.z = w.z - lx * s + lz * c;
      return out;
    },
    heading: (local) => local + w.yaw,
    update(dt) {
      t += dt;
      // The sodium lamp hums: a slow breath and the odd flicker, in the key, the pool, the halo and the cone.
      const flick = Math.sin(t * 37) * Math.sin(t * 11.3) > 0.985 ? 0.72 : 1;
      const k = (0.94 + 0.06 * Math.sin(t * 1.7)) * flick;
      key.intensity = KEY_POWER * k;
      poolMat.opacity = 0.2 * k;
      lampHaloMat.opacity = 0.8 * k;
      coneMat.uniforms.uK!.value = 0.035 * k;
      bulbMat.color.setHex(0xffc889).multiplyScalar(0.8 + 0.2 * k);
      lamp.intensity = LAMP_POWER * k;
      // the drizzle falls, wrapping at the ground
      for (let i = 0; i < RAIN; i++) {
        let y = rainPos[i * 6 + 1]! - rainVel[i]! * dt;
        if (y < 0) y += LAMP.y;
        rainPos[i * 6 + 1] = y;
        rainPos[i * 6 + 4] = y - 0.42;
      }
      rainGeo.getAttribute('position').needsUpdate = true;
      // the moths: loops round the bulb, each its own figure
      for (let i = 0; i < MOTHS; i++) {
        const m = mothSeed[i]!;
        const p = t * m.s;
        mothPos[i * 3] = LAMP.x + Math.sin(p + m.a) * m.ra + Math.sin(p * 2.3 + m.b) * 0.05;
        mothPos[i * 3 + 1] = LAMP.y - 0.12 + Math.sin(p * 1.3 + m.b) * m.rb * 0.6;
        mothPos[i * 3 + 2] = LAMP.z - 0.35 + Math.cos(p + m.a) * m.rb;
      }
      mothGeo.getAttribute('position').needsUpdate = true;
      // the vapour drifts on the GPU's clock; the sodium flicker shows in it too
      vapourMat.uniforms.uTime!.value = t;
      vapourMat.uniforms.uAlpha!.value = 0.1 * (0.85 + 0.15 * k);
      // the cat: sits a while (its tail swings, it blinks), then walks to the other spot, hopping on or off the bin
      const c = catState;
      c.timer -= dt;
      if (c.mode === 'sit') {
        c.tailTimer -= dt;
        if (c.tailTimer <= 0) {
          c.tailUp = !c.tailUp;
          c.tailTimer = c.tailUp ? 0.9 + catRnd() * 1.6 : 2 + catRnd() * 5;
          catFrame(c.tailUp ? 1 : 0);
        }
        if (c.timer <= 0) {
          c.mode = 'walk';
          c.from = CAT_SPOTS[c.spot]!;
          c.spot = c.spot === 0 ? 1 : 0;
          c.to = CAT_SPOTS[c.spot]!;
          c.walkT = 0;
          c.walkDur = Math.hypot(c.to.x - c.from.x, c.to.z - c.from.z) / 0.55;
          cat.scale.x = c.to.x < c.from.x ? 1 : -1; // the cells face −x; flip for the other way
        }
      } else {
        c.walkT += dt / c.walkDur;
        const u = Math.min(1, c.walkT);
        // the hop on or off the bin is the first / last third of the way: an arc over the ground path
        c.x = c.from.x + (c.to.x - c.from.x) * u;
        c.z = c.from.z + (c.to.z - c.from.z) * u;
        const hop = c.from.y > c.to.y ? Math.max(0, 1 - u * 3) : Math.max(0, u * 3 - 2);
        const lift = c.from.y > c.to.y ? c.from.y : c.to.y;
        c.y = lift * hop + Math.sin(hop * Math.PI) * 0.18;
        catFrame(2 + (Math.floor(c.walkT * c.walkDur * 6) % 4));
        if (u >= 1) {
          c.mode = 'sit';
          c.timer = 12 + catRnd() * 22;
          c.tailTimer = 1 + catRnd() * 3;
          c.tailUp = false;
          cat.scale.x = 1;
          catFrame(0);
        }
      }
      cat.position.set(c.x, c.y + CAT_SIZE / 2 - 0.045, c.z);
      // eyeshine while it sits, with a blink now and then; dark while it walks (seen in profile)
      c.blink -= dt;
      if (c.blink <= -0.14) c.blink = 2.5 + catRnd() * 5;
      const sitting = c.mode === 'sit';
      eyes.visible = sitting && c.blink > 0;
      if (sitting) {
        const [hx, hy] = CAT_HEAD[c.tailUp ? 1 : 0]!;
        const m = CAT_SIZE / CAT_PX;
        // the card is flipped in x for the camera (rotation.y = π), so cell x runs the other way in world x
        const ex = c.x + (CAT_PX / 2 - hx) * m;
        const ey = c.y + CAT_SIZE / 2 - 0.045 + (CAT_PX / 2 - hy) * m;
        eyePos.set([ex - 0.035, ey, c.z - 0.01, ex + 0.035, ey, c.z - 0.01]);
        eyeGeo.getAttribute('position').needsUpdate = true;
      }
    },
    dispose() {
      scene.environment = null;
      for (const d of disposables) d.dispose();
      for (const g of geos) g.dispose();
      key.shadow.map?.dispose();
      scene.clear();
    },
  };
}
